import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import UploadChungTu from "components/UploadChungTu";
import XemChungTuTrongApp from "components/XemChungTuTrongApp";
import { giaoDichApi } from "api/giaoDich";
import { chuVuonApi } from "api/chuVuon";
import { nganHangApi } from "api/nganHang";
import { MAU, FONT_HIEU, FONT_CHU } from "assets/theme/avocadoBrand";
import { nhanTrangThaiThanhToan } from "lib/nhanTrangThaiThanhToan";
import {
  timNganHangTheoTen,
  taoUrlAnhVietQr,
  taoNoiDungChuyenKhoanKhongDau,
} from "lib/vietQrChuyenKhoan";
import {
  useDialogFullManHinh,
  oDialogContentFormDai,
  oDialogActionsFormDai,
  oDialogPaperFormDai,
} from "lib/dungDialogFullManHinh";

function Nhan({ children }) {
  return (
    <MDTypography
      sx={{
        display: "block",
        mb: 0.25,
        fontFamily: FONT_CHU,
        fontWeight: 600,
        fontSize: "0.65rem",
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: alpha(MAU.ink, 0.55),
      }}
    >
      {children}
    </MDTypography>
  );
}

Nhan.propTypes = {
  children: PropTypes.node.isRequired,
};

function Don({ nhan, giaTri, xs }) {
  return (
    <Grid item xs={xs}>
      <MDBox sx={{ wordBreak: "break-word" }}>
        <MDTypography
          component="span"
          sx={{
            fontFamily: FONT_CHU,
            fontWeight: 600,
            fontSize: "0.68rem",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: alpha(MAU.ink, 0.55),
          }}
        >
          {nhan}:{" "}
        </MDTypography>
        <MDTypography
          component="span"
          sx={{
            fontFamily: FONT_HIEU,
            fontWeight: 600,
            fontSize: "0.95rem",
            color: MAU.ink,
          }}
        >
          {giaTri ?? "—"}
        </MDTypography>
      </MDBox>
    </Grid>
  );
}

Don.propTypes = {
  nhan: PropTypes.string.isRequired,
  giaTri: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  xs: PropTypes.number,
};

Don.defaultProps = {
  giaTri: null,
  xs: 6,
};

function HangSaoChep({ nhan, hienThi, giaTriCopy, id, dangCopyId, onSaoChep }) {
  const daCopy = dangCopyId === id;
  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1} py={0.5}>
      <MDBox sx={{ minWidth: 0 }}>
        <Nhan>{nhan}</Nhan>
        <MDTypography
          sx={{
            fontFamily: FONT_CHU,
            fontWeight: 600,
            fontSize: "0.85rem",
            color: MAU.ink,
            wordBreak: "break-word",
          }}
        >
          {hienThi}
        </MDTypography>
      </MDBox>
      <MDButton
        variant="text"
        color={daCopy ? "success" : "dark"}
        size="small"
        onClick={() => onSaoChep(giaTriCopy, id)}
        sx={{ flexShrink: 0, fontFamily: FONT_CHU }}
      >
        <Icon sx={{ mr: 0.5 }} fontSize="small">
          {daCopy ? "check" : "content_copy"}
        </Icon>
        {daCopy ? "Đã chép" : "Chép"}
      </MDButton>
    </MDBox>
  );
}

HangSaoChep.propTypes = {
  nhan: PropTypes.string.isRequired,
  hienThi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  giaTriCopy: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  id: PropTypes.string.isRequired,
  dangCopyId: PropTypes.string,
  onSaoChep: PropTypes.func.isRequired,
};

HangSaoChep.defaultProps = {
  dangCopyId: null,
};

function DongLoaiBo({ ten, khoiLuong, donGia, thanhTien, maSotHienThi, coVachTren }) {
  return (
    <MDBox
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 1,
        py: 0.75,
        borderTop: coVachTren ? `1px dashed ${alpha(MAU.ink, 0.15)}` : "none",
      }}
    >
      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography
          sx={{ fontFamily: FONT_CHU, fontWeight: 600, fontSize: "0.85rem", color: MAU.ink }}
        >
          {ten}
        </MDTypography>
        <MDTypography
          sx={{ fontFamily: FONT_CHU, fontSize: "0.72rem", color: alpha(MAU.ink, 0.55) }}
        >
          {khoiLuong} kg × {donGia.toLocaleString("vi-VN")} đ/kg
          {maSotHienThi ? ` · Sọt: ${maSotHienThi}` : ""}
        </MDTypography>
      </MDBox>
      <MDTypography
        sx={{
          fontFamily: FONT_HIEU,
          fontWeight: 600,
          fontSize: "1rem",
          color: MAU.skin,
          flexShrink: 0,
        }}
      >
        {thanhTien.toLocaleString("vi-VN")} đ
      </MDTypography>
    </MDBox>
  );
}

DongLoaiBo.propTypes = {
  ten: PropTypes.string.isRequired,
  khoiLuong: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  donGia: PropTypes.number.isRequired,
  thanhTien: PropTypes.number.isRequired,
  maSotHienThi: PropTypes.string,
  coVachTren: PropTypes.bool.isRequired,
};

DongLoaiBo.defaultProps = {
  maSotHienThi: null,
};

function ChiTietHangChoThanhToan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fullManHinh = useDialogFullManHinh();

  const [giaoDich, setGiaoDich] = useState(null);
  const [chuVuon, setChuVuon] = useState(null);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState("");
  const [dangLuu, setDangLuu] = useState(false);
  const [chungTuUrl, setChungTuUrl] = useState(null);
  const [daCopyId, setDaCopyId] = useState(null);

  const [moTraLai, setMoTraLai] = useState(false);
  const [ghiChuTraLai, setGhiChuTraLai] = useState("");
  const [danhSachNganHang, setDanhSachNganHang] = useState([]);

  useEffect(() => {
    nganHangApi
      .danhSach()
      .then(setDanhSachNganHang)
      .catch(() => setDanhSachNganHang([]));
  }, []);

  useEffect(() => {
    let huy = false;
    setDangTai(true);
    setLoi("");
    setGiaoDich(null);
    setChuVuon(null);

    const huyLangNghe = giaoDichApi.langNgheChiTiet(
      id,
      async (gd) => {
        if (huy) return;
        setGiaoDich(gd);
        setChungTuUrl(gd?.chungTuUrl ?? null);
        setDangTai(false);
        if (!gd?.maChuVuon) {
          setChuVuon(null);
          return;
        }
        try {
          const cv = await chuVuonApi.chiTiet(gd.maChuVuon);
          if (!huy) setChuVuon(cv);
        } catch {
          if (!huy) setChuVuon(null);
        }
      },
      (err) => {
        if (huy) return;
        setLoi(err.message);
        setGiaoDich(null);
        setDangTai(false);
      }
    );

    return () => {
      huy = true;
      huyLangNghe();
    };
  }, [id]);

  const tongThanhTien = useMemo(
    () =>
      giaoDich ? (giaoDich.items || []).reduce((tong, it) => tong + (it.thanhTien || 0), 0) : 0,
    [giaoDich]
  );

  const diaChiChuVuon = useMemo(() => {
    if (!chuVuon) return "";
    return [chuVuon.diaChi, chuVuon.thon, chuVuon.xa, chuVuon.tinh].filter(Boolean).join(", ");
  }, [chuVuon]);

  const tt = giaoDich?.trangThaiThanhToan;
  const choKeToan = tt === "cho_ke_toan";
  const dangThanhToan = tt === "dang_thanh_toan";
  const daThanhToan = tt === "da_thanh_toan";
  const trongHangCho = choKeToan || dangThanhToan;
  const nhanTt = nhanTrangThaiThanhToan(tt);

  const noiDungCk = useMemo(() => {
    if (!giaoDich) return "";
    return taoNoiDungChuyenKhoanKhongDau({
      tenChuVuon: giaoDich.tenChuVuon,
      thoiGian: giaoDich.thoiGian,
    });
  }, [giaoDich]);

  const nganHangKhop = useMemo(
    () => timNganHangTheoTen(chuVuon?.tenNganHang, danhSachNganHang),
    [chuVuon?.tenNganHang, danhSachNganHang]
  );

  const urlQrCk = useMemo(() => {
    if (!chuVuon?.soTkNganHang || !nganHangKhop?.bin) return "";
    return taoUrlAnhVietQr({
      bin: nganHangKhop.bin,
      soTk: chuVuon.soTkNganHang,
      soTien: tongThanhTien,
      noiDung: noiDungCk,
      tenChuTk: chuVuon.chuTaiKhoan || chuVuon.hoTen || "",
    });
  }, [chuVuon, nganHangKhop, tongThanhTien, noiDungCk]);

  async function saoChep(giaTri, copyId) {
    try {
      await navigator.clipboard.writeText(String(giaTri));
      setDaCopyId(copyId);
      setTimeout(() => setDaCopyId((cur) => (cur === copyId ? null : cur)), 1500);
    } catch {
      setLoi("Không sao chép được — trình duyệt chặn quyền clipboard.");
    }
  }

  async function nhanThongTin() {
    setLoi("");
    setDangLuu(true);
    try {
      await giaoDichApi.nhanThongTinThanhToan(id);
      setGiaoDich((gd) =>
        gd
          ? {
              ...gd,
              trangThaiThanhToan: "dang_thanh_toan",
              thoiGianNhanThongTin: new Date().toISOString(),
            }
          : gd
      );
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuu(false);
    }
  }

  async function xacNhanDaThanhToan() {
    setLoi("");
    if (!chungTuUrl) {
      setLoi("Bắt buộc đính kèm chứng từ trước khi xác nhận đã thanh toán.");
      return;
    }
    setDangLuu(true);
    try {
      await giaoDichApi.xacNhanDaThanhToan(id, { chungTuUrl });
      navigate("/bao-cao/hang-cho-thanh-toan");
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuu(false);
    }
  }

  async function xacNhanTraLai() {
    setLoi("");
    const ghiChu = ghiChuTraLai.trim();
    if (!ghiChu) {
      setLoi("Bắt buộc nhập ghi chú khi trả lại.");
      return;
    }
    setDangLuu(true);
    try {
      await giaoDichApi.traLaiChoXuLy(id, { ghiChuThanhToan: ghiChu });
      setMoTraLai(false);
      navigate("/bao-cao/hang-cho-thanh-toan");
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuu(false);
    }
  }

  if (dangTai) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDTypography variant="button" color="text">
            Đang tải...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!giaoDich) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDAlert color="error">{loi || "Không tìm thấy giao dịch."}</MDAlert>
          <MDBox mt={2}>
            <MDButton
              component={Link}
              to="/bao-cao/hang-cho-thanh-toan"
              variant="outlined"
              color="dark"
            >
              Quay lại hàng chờ
            </MDButton>
          </MDBox>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!trongHangCho && !daThanhToan) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDAlert color="info">
            Phiếu này không còn trong hàng chờ (trạng thái: {nhanTt.label}).
          </MDAlert>
          <MDBox mt={2}>
            <MDButton
              component={Link}
              to="/bao-cao/hang-cho-thanh-toan"
              variant="gradient"
              color="info"
            >
              Quay lại hàng chờ
            </MDButton>
          </MDBox>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={11} xl={10}>
            <MDBox mb={1.5}>
              <MDButton
                component={Link}
                to="/bao-cao/hang-cho-thanh-toan"
                variant="text"
                color="dark"
                size="small"
                sx={{ fontFamily: FONT_CHU }}
              >
                <Icon sx={{ mr: 0.5 }} fontSize="small">
                  arrow_back
                </Icon>
                Hàng chờ thanh toán
              </MDButton>
            </MDBox>

            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: { xs: 0, md: 0 }, height: "100%", borderRadius: "16px" }}>
                  <MDBox p={2.5}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={1}
                      mb={1.5}
                    >
                      <MDTypography
                        sx={{
                          fontFamily: FONT_HIEU,
                          fontWeight: 600,
                          fontSize: "1.35rem",
                          color: MAU.ink,
                        }}
                      >
                        {daThanhToan ? "Chi tiết thanh toán" : "Xử lý thanh toán"}
                      </MDTypography>
                      <MDBadge
                        badgeContent={nhanTt.label}
                        color={nhanTt.color}
                        variant="gradient"
                        size="sm"
                      />
                    </MDBox>

                    <Grid container rowSpacing={0.5} columnSpacing={1.5}>
                      <Don nhan="Chủ vườn" giaTri={giaoDich.tenChuVuon} xs={12} />
                      <Don nhan="NV thu mua" giaTri={giaoDich.tenNhanVien} xs={12} />
                      <Don nhan="Điện thoại" giaTri={chuVuon?.sdt} xs={12} />
                      <Don nhan="Vườn" giaTri={giaoDich.tenVuon} xs={12} />
                      <Don nhan="Địa chỉ" giaTri={diaChiChuVuon || null} xs={12} />
                      {giaoDich.maHopTacXa && (
                        <Don nhan="Hợp tác xã" giaTri={giaoDich.tenHopTacXa} xs={12} />
                      )}
                      <Don
                        nhan="Thời gian"
                        giaTri={new Date(giaoDich.thoiGian).toLocaleString("vi-VN")}
                        xs={12}
                      />
                    </Grid>

                    <MDBox sx={{ mt: 1.5 }}>
                      {(giaoDich.items || []).map((it, idx) => (
                        <DongLoaiBo
                          key={idx}
                          ten={it.tenLoaiBo}
                          khoiLuong={it.khoiLuong}
                          donGia={it.donGia}
                          thanhTien={it.thanhTien}
                          maSotHienThi={it.maSotHienThi}
                          coVachTren={idx > 0}
                        />
                      ))}
                    </MDBox>

                    <MDBox
                      sx={{
                        mt: 0.5,
                        pt: 1.5,
                        borderTop: `1.5px dashed ${alpha(MAU.ink, 0.18)}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                      }}
                    >
                      <MDTypography
                        sx={{
                          fontFamily: FONT_CHU,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: alpha(MAU.ink, 0.6),
                        }}
                      >
                        Tổng thành tiền
                      </MDTypography>
                      <MDTypography
                        sx={{
                          fontFamily: FONT_HIEU,
                          fontWeight: 600,
                          fontSize: "1.4rem",
                          color: MAU.skin,
                        }}
                      >
                        {tongThanhTien.toLocaleString("vi-VN")} đ
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%", borderRadius: "16px" }}>
                  <MDBox p={2.5}>
                    <MDTypography
                      sx={{
                        fontFamily: FONT_HIEU,
                        fontWeight: 600,
                        fontSize: "1.2rem",
                        color: MAU.ink,
                        mb: 1.5,
                      }}
                    >
                      Thông tin chuyển khoản
                    </MDTypography>
                    {!chuVuon?.tenNganHang && (
                      <MDBox mb={2}>
                        <MDAlert color="warning">
                          Chủ vườn chưa có thông tin ngân hàng — kiểm tra lại với nhân viên thu mua
                          hoặc trang Quản lý Chủ vườn.
                        </MDAlert>
                      </MDBox>
                    )}
                    {chuVuon?.tenNganHang && !chuVuon?.soTkNganHang && (
                      <MDBox mb={2}>
                        <MDAlert color="warning">
                          Thiếu số tài khoản — không tạo được mã QR.
                        </MDAlert>
                      </MDBox>
                    )}
                    {chuVuon?.tenNganHang && chuVuon?.soTkNganHang && !nganHangKhop?.bin && (
                      <MDBox mb={2}>
                        <MDAlert color="warning">
                          Không map được mã ngân hàng (BIN) — vẫn có thể chép tay STK / số tiền.
                        </MDAlert>
                      </MDBox>
                    )}

                    <MDBox p={1.5} mb={0} borderRadius="md" sx={{ backgroundColor: "#f0f2f5" }}>
                      <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={urlQrCk ? 6 : 12} sx={{ minWidth: 0 }}>
                          <Grid container rowSpacing={0.5} columnSpacing={1.5}>
                            <Don
                              nhan="Chủ tài khoản"
                              giaTri={chuVuon?.chuTaiKhoan || chuVuon?.hoTen}
                              xs={12}
                            />
                            <Don nhan="Ngân hàng" giaTri={chuVuon?.tenNganHang} xs={12} />
                          </Grid>
                          <Divider sx={{ my: 0.75 }} />
                          <HangSaoChep
                            nhan="Số tài khoản"
                            hienThi={chuVuon?.soTkNganHang ?? "—"}
                            giaTriCopy={chuVuon?.soTkNganHang ?? ""}
                            id="stk"
                            dangCopyId={daCopyId}
                            onSaoChep={saoChep}
                          />
                          <HangSaoChep
                            nhan="Số tiền"
                            hienThi={`${tongThanhTien.toLocaleString("vi-VN")} đ`}
                            giaTriCopy={tongThanhTien}
                            id="tien"
                            dangCopyId={daCopyId}
                            onSaoChep={saoChep}
                          />
                          <HangSaoChep
                            nhan="Nội dung"
                            hienThi={noiDungCk || "—"}
                            giaTriCopy={noiDungCk}
                            id="noidung"
                            dangCopyId={daCopyId}
                            onSaoChep={saoChep}
                          />
                        </Grid>
                        {urlQrCk && (
                          <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="center"
                              sx={{ width: "100%", maxWidth: 315, mx: "auto" }}
                            >
                              <MDBox
                                component="img"
                                src={urlQrCk}
                                alt="QR chuyển khoản VietQR"
                                sx={{
                                  width: "100%",
                                  maxWidth: 315,
                                  aspectRatio: "1 / 1",
                                  height: "auto",
                                  objectFit: "contain",
                                  borderRadius: "8px",
                                  backgroundColor: "#fff",
                                  display: "block",
                                }}
                              />
                              <MDTypography
                                sx={{
                                  mt: 0.75,
                                  fontFamily: FONT_CHU,
                                  fontSize: "0.75rem",
                                  color: alpha(MAU.ink, 0.6),
                                  textAlign: "center",
                                }}
                              >
                                Quét để chuyển khoản
                              </MDTypography>
                            </MDBox>
                          </Grid>
                        )}
                      </Grid>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ borderRadius: "16px" }}>
                  <MDBox p={2.5}>
                    {daThanhToan && (
                      <>
                        <MDTypography
                          sx={{
                            fontFamily: FONT_HIEU,
                            fontWeight: 600,
                            fontSize: "1.2rem",
                            color: MAU.ink,
                            mb: 1.5,
                          }}
                        >
                          Đã thanh toán
                        </MDTypography>
                        <Grid container rowSpacing={0.5} columnSpacing={1.5} mb={1.5}>
                          <Don
                            nhan="Thời gian TT"
                            giaTri={
                              giaoDich.thoiGianThanhToan
                                ? new Date(giaoDich.thoiGianThanhToan).toLocaleString("vi-VN")
                                : null
                            }
                            xs={12}
                          />
                          {giaoDich.ghiChuThanhToan && (
                            <Don nhan="Ghi chú" giaTri={giaoDich.ghiChuThanhToan} xs={12} />
                          )}
                        </Grid>
                        {giaoDich.chungTuUrl ? (
                          <XemChungTuTrongApp url={giaoDich.chungTuUrl} />
                        ) : (
                          <MDAlert color="warning">Không có chứng từ đính kèm.</MDAlert>
                        )}
                      </>
                    )}

                    {dangThanhToan && (
                      <MDBox mb={1.5}>
                        <UploadChungTu
                          giaoDichId={id}
                          value={chungTuUrl}
                          onChange={setChungTuUrl}
                          batBuoc
                        />
                      </MDBox>
                    )}

                    {loi && (
                      <MDBox mb={1.5}>
                        <MDAlert color="error">{loi}</MDAlert>
                      </MDBox>
                    )}

                    {trongHangCho && (
                      <MDBox display="flex" flexDirection="column" gap={1}>
                        {choKeToan && (
                          <MDButton
                            variant="gradient"
                            color="info"
                            fullWidth
                            onClick={nhanThongTin}
                            disabled={dangLuu}
                            sx={{ fontFamily: FONT_CHU, fontWeight: 600 }}
                          >
                            {dangLuu ? "Đang lưu..." : "Đã nhận thông tin"}
                          </MDButton>
                        )}
                        {dangThanhToan && (
                          <MDButton
                            variant="gradient"
                            color="success"
                            fullWidth
                            onClick={xacNhanDaThanhToan}
                            disabled={dangLuu || !chungTuUrl}
                            sx={{ fontFamily: FONT_CHU, fontWeight: 600 }}
                          >
                            {dangLuu ? "Đang lưu..." : "Đã thanh toán"}
                          </MDButton>
                        )}
                        <MDButton
                          variant="outlined"
                          color="error"
                          fullWidth
                          onClick={() => {
                            setGhiChuTraLai("");
                            setLoi("");
                            setMoTraLai(true);
                          }}
                          disabled={dangLuu}
                          sx={{ fontFamily: FONT_CHU, fontWeight: 600 }}
                        >
                          Trả lại
                        </MDButton>
                      </MDBox>
                    )}
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog
        open={moTraLai}
        onClose={() => !dangLuu && setMoTraLai(false)}
        fullScreen={fullManHinh}
        fullWidth
        maxWidth="sm"
        PaperProps={oDialogPaperFormDai}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, flexShrink: 0 }}>
          <MDTypography variant="h5" component="h2">
            Trả lại phiếu
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" mt={0.5}>
            Phiếu về lại &quot;Chờ xử lý&quot; để nhân viên thu mua sửa. Ghi chú bắt buộc.
          </MDTypography>
        </DialogTitle>
        <DialogContent sx={oDialogContentFormDai}>
          <MDInput
            label="Ghi chú trả lại"
            value={ghiChuTraLai}
            onChange={(e) => setGhiChuTraLai(e.target.value)}
            multiline
            rows={3}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={oDialogActionsFormDai}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setMoTraLai(false)}
            disabled={dangLuu}
          >
            Huỷ
          </MDButton>
          <MDButton
            variant="gradient"
            color="error"
            onClick={xacNhanTraLai}
            disabled={dangLuu || !ghiChuTraLai.trim()}
          >
            {dangLuu ? "Đang trả..." : "Trả lại"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default ChiTietHangChoThanhToan;
